import { useEffect } from "react";
import { useCreate, useList, useNavigation, useUpdate } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { Button, Checkbox, DatePicker, Form, Input, message } from "antd";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { AppTitle } from "components";
import { MedicineAnnouncement, MedicineAnnouncementFormValues } from "./types";

const DATE_FORMAT = "YYYY-MM-DD";
const DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";

export const MedicineAnnouncementFormPage = () => {
  const { push } = useNavigation();
  const params = useParams();
  const announcementId = params.id ? Number(params.id) : undefined;
  const isEdit = Boolean(announcementId);
  const [form] = Form.useForm<MedicineAnnouncementFormValues>();

  const { data, isLoading } = useList<MedicineAnnouncement>({
    resource: "medicine-announcements",
    dataProviderName: "medicineAnnouncementsDataProvider",
    queryOptions: {
      enabled: isEdit,
    },
  });
  const { mutate: createAnnouncement, isLoading: creatingAnnouncement } =
    useCreate();
  const { mutate: updateAnnouncement, isLoading: updatingAnnouncement } =
    useUpdate();

  useEffect(() => {
    const announcement = data?.data.find((item) => item.id === announcementId);

    if (!announcement) {
      return;
    }

    form.setFieldsValue({
      id: announcement.id,
      name: announcement.name,
      eventDateFrom: dayjs(announcement.eventDateFrom, DATE_FORMAT) as any,
      eventDateTo: announcement.eventDateTo
        ? (dayjs(announcement.eventDateTo, DATE_FORMAT) as any)
        : undefined,
      place: announcement.place,
      linkUrl: announcement.linkUrl || "",
      isActive: announcement.isActive,
    });
  }, [announcementId, data?.data, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      id: isEdit ? announcementId : null,
      eventDateFrom: dayjs(values.eventDateFrom).format(DATE_FORMAT),
      eventDateTo: values.eventDateTo
        ? dayjs(values.eventDateTo).format(DATE_FORMAT)
        : null,
      linkUrl: values.linkUrl?.trim() || null,
      isActive: values.isActive ?? false,
    };
    const callbacks = {
      onSuccess: () => {
        message.success("Анонс сохранен");
        push("/medicine/materials");
      },
      onError: (error: any) => {
        message.error(error?.message || "Не удалось сохранить анонс");
      },
    };

    if (isEdit && announcementId) {
      updateAnnouncement(
        {
          dataProviderName: "medicineAnnouncementsDataProvider",
          resource: "medicine-announcements",
          id: announcementId,
          values: payload,
        },
        callbacks
      );
      return;
    }

    createAnnouncement(
      {
        dataProviderName: "medicineAnnouncementsDataProvider",
        resource: "medicine-announcements",
        values: payload,
      },
      callbacks
    );
  };

  return (
    <Show
      isLoading={isLoading}
      title={
        <AppTitle canGoBack>
          {isEdit ? "Редактирование анонса" : "Создание анонса"}
        </AppTitle>
      }
      breadcrumb={false}
      headerButtons={<></>}
      footerButtons={null}
      goBack={false}
    >
      <Form<MedicineAnnouncementFormValues>
        form={form}
        layout="vertical"
        initialValues={{ isActive: true }}
      >
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: "Введите название" }]}
        >
          <Input placeholder="Конференция" />
        </Form.Item>
        <Form.Item
          name="eventDateFrom"
          label="Дата начала события"
          rules={[{ required: true, message: "Выберите дату начала события" }]}
        >
          <DatePicker format={DATE_FORMAT} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="eventDateTo" label="Дата окончания события">
          <DatePicker format={DATE_FORMAT} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="place"
          label="Место"
          rules={[{ required: true, message: "Введите место" }]}
        >
          <Input placeholder="Санкт-Петербург" />
        </Form.Item>

        <Form.Item
          name="linkUrl"
          label="Ссылка на мероприятие"
          extra="По клику на карточку на сайте откроется эта ссылка. Без ссылки откроется описание анонса"
          rules={[
            {
              validator: (_, value) =>
                !value?.trim() || /^https?:\/\//.test(value.trim())
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        "Ссылка должна начинаться с http:// или https://"
                      )
                    ),
            },
          ]}
        >
          <Input placeholder="https://" />
        </Form.Item>

        <Form.Item name="isActive" valuePropName="checked">
          <Checkbox>Активен</Checkbox>
        </Form.Item>

        <Button
          type="primary"
          loading={creatingAnnouncement || updatingAnnouncement}
          onClick={handleSubmit}
        >
          Сохранить
        </Button>
      </Form>
    </Show>
  );
};
